import React from "react";
import { Button, Table, Tooltip } from "antd";
import {
  PnlMonthColumn,
  PnlRowKind,
  fmtINR,
  fmtINRCompact,
  fmtMonth,
  num,
} from "./pnlShared";

// The month-pivot P&L table (URI "F-1 Top Sheet-P&L" layout): particulars down
// the left, one column per month, Total fixed right. Placeholder heads
// (Escalation, staff Manpower, Statutory) are muted em-dashes until the
// overhead data push — never clickable. Computed cells navigate to the Month
// Detail page via onCellClick(month, rowKind).

interface PnlTableRowModel {
  key: PnlRowKind;
  section: "INFLOW" | "OUTFLOW" | "RESULT";
  label: string;
  hint?: string;         // tooltip on the row label
  placeholder?: boolean; // untracked head — renders an em-dash
  muted?: boolean;       // tracked but pending feed — renders the real (zero) value, greyed
  emphasis?: boolean;    // bold total rows
  clickable: boolean;
  field: keyof PnlMonthColumn;
}

const ROWS: PnlTableRowModel[] = [
  { key: "inflow", section: "INFLOW", label: "Work Bill", clickable: true, field: "inflowAmount" },
  { key: "escalation", section: "INFLOW", label: "Escalation", placeholder: true, clickable: false, field: "escalationAmount" },
  { key: "totalInflow", section: "INFLOW", label: "Total Inflow", emphasis: true, clickable: true, field: "totalInflow" },
  { key: "salaries", section: "OUTFLOW", label: "Manpower Cost (Staff)", placeholder: true, clickable: false, field: "salariesAmount" },
  { key: "machinery", section: "OUTFLOW", label: "Machinery Cost", clickable: true, field: "machineryAmount" },
  { key: "material", section: "OUTFLOW", label: "Material Cost", clickable: true, field: "materialAmount" },
  {
    key: "subconPrw",
    section: "OUTFLOW",
    label: "Manpower Cost",
    hint: "PRW & sub-contract crews (applied manpower norms) + task labour resources, scaled to the month's share. Staff salaries are the separate 'Manpower Cost (Staff)' head, coming with the overhead data push.",
    clickable: true,
    field: "subconPrwAmount",
  },
  { key: "statutory", section: "OUTFLOW", label: "Statutory Cost", placeholder: true, clickable: false, field: "statutoryAmount" },
  {
    key: "totalOutflow",
    section: "OUTFLOW",
    label: "TOTAL OUTFLOW (PLANNED)",
    hint: "Planned spend derived from monthly schedules × norms and rates.",
    emphasis: true,
    clickable: true,
    field: "totalOutflow",
  },
  {
    key: "actualOutflow",
    section: "OUTFLOW",
    label: "TOTAL OUTFLOW (ACTUAL)",
    hint: "Actual spend will be fed later (DPR / consumption / bills). Zero until then — the planned-vs-actual variance becomes readable right here once it lands.",
    muted: true,
    emphasis: true,
    clickable: false,
    field: "actualOutflow",
  },
  { key: "margin", section: "RESULT", label: "PROFIT", emphasis: true, clickable: true, field: "margin" },
  { key: "marginPct", section: "RESULT", label: "PROFIT %", clickable: false, field: "marginPct" },
];

const sectionBadge: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  color: "#8094ae",
  display: "block",
  lineHeight: 1.1,
};

const PLACEHOLDER_HINT = "Not tracked yet — coming with the overhead data push";

const PnlTopSheetTable: React.FC<{
  months: PnlMonthColumn[];
  totals?: PnlMonthColumn;
  onCellClick: (month: string, kind: PnlRowKind) => void;
}> = ({ months, totals, onCellClick }) => {
  // Static 12-row list — computed inline (a memo here survives hot reloads
  // with stale rows, hiding newly added heads until a hard refresh).
  const dataSource = ROWS.map((row, index) => ({
    ...row,
    rowKey: row.key,
    firstOfSection: index === 0 || ROWS[index - 1].section !== row.section,
  }));

  const renderAmountCell = (row: (typeof dataSource)[number], column?: PnlMonthColumn) => {
    if (!column) return null;
    if (row.key === "marginPct") {
      return (
        <span style={{ fontVariantNumeric: "tabular-nums", color: "#526484" }}>
          {num(column.marginPct).toFixed(1)}%
        </span>
      );
    }
    if (row.placeholder) {
      return (
        <Tooltip title={PLACEHOLDER_HINT}>
          <span style={{ color: "#c9d2e3" }}>—</span>
        </Tooltip>
      );
    }
    const value = num(column[row.field] as string);
    const color = row.muted
      ? "#8c94a8"
      : row.key === "margin"
      ? value >= 0
        ? "#237804"
        : "#cf1322"
      : row.emphasis
      ? "#022c57"
      : undefined;
    const label = (
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: row.emphasis ? 700 : 500,
          color,
        }}
      >
        {fmtINRCompact(value)}
      </span>
    );
    if (!row.clickable || column.month === "TOTAL") {
      return <Tooltip title={fmtINR(value)}>{label}</Tooltip>;
    }
    return (
      <Tooltip title={`${fmtINR(value)} — click for ${fmtMonth(column.month)} detail`}>
        <Button
          type="link"
          size="small"
          style={{ padding: 0, height: "auto" }}
          onClick={() => onCellClick(column.month, row.key)}
        >
          {label}
        </Button>
      </Tooltip>
    );
  };

  const columns: any[] = [
    {
      title: "Particulars",
      key: "label",
      fixed: "left" as const,
      width: 230,
      render: (_: any, row: (typeof dataSource)[number]) => (
        <div>
          {row.firstOfSection && <span style={sectionBadge}>{row.section}</span>}
          <Tooltip title={row.hint}>
            <span
              style={{
                fontWeight: row.emphasis ? 700 : 500,
                color: row.placeholder ? "#8c94a8" : "#022c57",
                ...(row.hint
                  ? { textDecoration: "underline dotted #b6c2d9", textUnderlineOffset: 3 }
                  : {}),
              }}
            >
              {row.label}
            </span>
          </Tooltip>
        </div>
      ),
    },
    ...months.map((column) => ({
      title: fmtMonth(column.month),
      key: column.month,
      align: "right" as const,
      width: 128,
      render: (_: any, row: (typeof dataSource)[number]) => renderAmountCell(row, column),
    })),
    {
      title: "Total",
      key: "total",
      fixed: "right" as const,
      align: "right" as const,
      width: 150,
      onCell: () => ({ style: { background: "#f7f9fc" } }),
      render: (_: any, row: (typeof dataSource)[number]) => renderAmountCell(row, totals),
    },
  ];

  return (
    <Table
      size="small"
      bordered
      sticky
      className="customTable"
      rowKey="rowKey"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{ x: 230 + 128 * months.length + 150 }}
    />
  );
};

export default PnlTopSheetTable;
